import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Diamond, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function Register() {
  const { register } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success(t("auth.accountCreated"));
      nav("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-obsidian text-white">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-10">
            <Link to="/" className="flex items-center gap-2"><img src="/hephy-logo.png" alt="Hephy 3D" className="w-8 h-8 object-contain" /><span className="font-serif text-xl">Hephy 3D</span></Link>
            <LanguageSwitcher />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">{t("auth.registerTitle")}</h1>
          <p className="text-white/50 mt-2 mb-8 text-sm">{t("auth.registerSubtitle")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-white/70">{t("auth.name")}</Label>
              <Input data-testid="register-name" required value={form.name} onChange={set("name")} className="mt-2 bg-white/5 border-white/15 h-11" />
            </div>
            <div>
              <Label className="text-white/70">{t("auth.business")}</Label>
              <Input data-testid="register-business" value={form.business_name} onChange={set("business_name")} className="mt-2 bg-white/5 border-white/15 h-11" placeholder={t("auth.businessPlaceholder")} />
            </div>
            <div>
              <Label className="text-white/70">{t("auth.email")}</Label>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className="mt-2 bg-white/5 border-white/15 h-11" />
            </div>
            <div>
              <Label className="text-white/70">{t("auth.password")}</Label>
              <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-2 bg-white/5 border-white/15 h-11" placeholder={t("auth.passwordHint")} />
            </div>
            <Button data-testid="register-submit" disabled={loading} className="w-full h-11 rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold">
              {loading ? <CircleNotch className="animate-spin" /> : t("auth.createAccount")}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-5"><div className="h-px bg-white/10 flex-1" /><span className="text-xs text-white/40">o</span><div className="h-px bg-white/10 flex-1" /></div>
          <Button type="button" data-testid="register-google" onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/dashboard";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }} variant="outline" className="w-full h-11 rounded-full border-white/15 bg-white/5 hover:bg-white/10 font-medium flex items-center justify-center gap-2">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continuar con Google
          </Button>
          <p className="text-sm text-white/50 mt-6">{t("auth.haveAccount")} <Link to="/login" className="text-gold hover:underline">{t("auth.signInLink")}</Link></p>
        </div>
      </div>
      <div className="hidden lg:block relative grain order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1624585179018-25699030cb8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
      </div>
    </div>
  );
}
